import * as React from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { PrimitiveAtom } from 'jotai';
import { ArrowTopRightOnSquareIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import { jobPositionApiDataAtom } from './job-position/job-position.atoms';

import { Button, Modal, ModalContent } from '@restworld/ui-ds-base';
import { TimelineV2 } from './legacy';
import { customImageLoader, getAssetSrc, GreenCheckMarkWithWhiteBg } from '@restworld/utils-common';
import type { JobApplication, JobPosition } from '@restworld/utility-types';

import { useSaveResearchModalForJobPosition } from './worker-entry-point/worker-entry-point-modal';
import { inAppInterviewModalJobPositionIdAtom } from './worker-entry-point/worker-entry-point.atoms';
import { preInterviewHref } from './worker-entry-point/worker-entry-point.components/in-app-assessment-confirmation';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IWorkerManagedUsingKlaaryoProps {}

export const workerManagedUsingKlaaryoModalFlagAtom = atom(false);

/**
 * The interview of the application this modal confirms, as far as the modal needs it.
 *
 * Only `channel === 'whatsapp'` draws the WhatsApp step ("Serena will write to you on
 * WhatsApp"). Every other value, `null` included, draws the in-app step: since 13/10/2026 the
 * rollout is at 100% and a new application is interviewed in the app, so an unknown channel is
 * far more likely to be in-app than WhatsApp, and promising a WhatsApp message that never
 * comes is the worse of the two mistakes. Whoever opens the modal sets this first.
 */
export type ConfirmedApplicationInterview = {
  channel: JobApplication['assessment_channel'];
  status?: JobApplication['assessment_status'];
  jobPositionId?: string;
  /**
   * The position the application belongs to, when the page that opens the modal is not that
   * position's page. Absent, the modal reads `jobPositionApiDataAtom`, which is only current on
   * `/posizione`: on `/profilo` it still holds whatever position was viewed last in the tab.
   */
  jobPosition?: ConfirmedJobPosition;
};
export type ConfirmedJobPosition = Pick<Partial<JobPosition>, 'ats_applyurl' | 'published_from_ats'>;
export const workerManagedInterviewAtom = atom<ConfirmedApplicationInterview>({
  channel: null,
}) as PrimitiveAtom<ConfirmedApplicationInterview>;

/**
 * The application the manual wizard created at its first step, with the ATS fields of its own
 * position, kept until the wizard ends on `/profilo?manage_using_klaaryo=true` (a client
 * navigation, so the store survives). `undefined` after a full reload: the profile then cannot
 * tell the channel and shows the in-app step.
 */
export type WizardCreatedApplication = {
  application?: Partial<JobApplication>;
  jobPosition: ConfirmedJobPosition;
};
export const wizardCreatedApplicationAtom = atom<WizardCreatedApplication | undefined>(undefined) as PrimitiveAtom<
  WizardCreatedApplication | undefined
>;

/**
 * Opens the confirmation for an application that was just created outside the entry-point
 * modal (phone verification on the position page, end of the manual wizard). The gate is the
 * same as `JobApplyConfirmation`'s: an ATS position keeps the ATS continuation, an application
 * interviewed in the app gets Modal B (into the chat), and everything else gets this modal,
 * whose interview step follows `workerManagedInterviewAtom`.
 */
export const useOpenApplicationConfirmation = () => {
  const setModalFlag = useSetAtom(workerManagedUsingKlaaryoModalFlagAtom);
  const setInterview = useSetAtom(workerManagedInterviewAtom);
  const setInAppInterviewJobPositionId = useSetAtom(inAppInterviewModalJobPositionIdAtom);
  const jpData = useAtomValue(jobPositionApiDataAtom);
  const isPageJobPositionAts = !!jpData?.ats_applyurl;

  return React.useCallback(
    (
      application: Partial<JobApplication> | undefined,
      options: { fallbackJobPositionId?: string; jobPosition?: ConfirmedJobPosition } = {}
    ) => {
      const { fallbackJobPositionId, jobPosition } = options;
      // The position page passes nothing and its atom is current; any other page passes the
      // application's own position so a stale atom cannot decide the screen.
      const isAts = jobPosition ? !!jobPosition.ats_applyurl : isPageJobPositionAts;
      const channel = application?.assessment_channel ?? null;
      const jobPositionId = application?.job_position_id ?? fallbackJobPositionId;
      if (!isAts && channel === 'in_app' && jobPositionId) {
        setInAppInterviewJobPositionId(jobPositionId);
        return;
      }
      setInterview({ channel, status: application?.assessment_status ?? null, jobPositionId, jobPosition });
      setModalFlag(true);
    },
    [isPageJobPositionAts, setInAppInterviewJobPositionId, setInterview, setModalFlag]
  );
};
export const workerManagedFooterMapKeyAtom = atom<'cv_applied' | 'default_applied' | 'direct_applied' | 'profile'>(
  'default_applied'
);
const WorkerManagedUsingKlaaryo: React.FunctionComponent<IWorkerManagedUsingKlaaryoProps> = () => {
  const [workerManagedUsingKlaaryoModalFlag, setWorkerManagedUsingKlaaryoModalFlagAtom] = useAtom(
    workerManagedUsingKlaaryoModalFlagAtom
  );
  const { t } = useTranslation('common');
  const { openSaveResearchModal } = useSaveResearchModalForJobPosition();
  const pageJobPosition = useAtomValue(jobPositionApiDataAtom);
  const { jobPosition } = useAtomValue(workerManagedInterviewAtom);
  // The application's own position when the opener passed it, the page's otherwise.
  const jpData: ConfirmedJobPosition = jobPosition ?? pageJobPosition ?? {};

  // No auto-redirect to the ATS apply URL on mount. The redirect must only
  // happen after the worker has explicitly registered + uploaded CV and then
  // clicked the "Continue on ATS" CTA below. Triggering window.open at mount
  // hijacks the JP detail page before the user has shown any intent to apply.

  const defaultFooter = React.useMemo(
    () =>
      jpData?.ats_applyurl ? (
        <Button
          fullwidth
          onClick={() => {
            window.open(jpData?.ats_applyurl, '_blank');
          }}
        >
          {t('continue_the_application_on_ats')}
        </Button>
      ) : (
        <Button
          onClick={() => {
            openSaveResearchModal();
            setWorkerManagedUsingKlaaryoModalFlagAtom(false);
          }}
          fullwidth
        >
          {t('receive_other_offers_similar_to_this')}
        </Button>
      ),
    [openSaveResearchModal, setWorkerManagedUsingKlaaryoModalFlagAtom, t, jpData?.ats_applyurl]
  );

  return (
    <Modal
      open={workerManagedUsingKlaaryoModalFlag}
      onOpenChange={(v) => !v && setWorkerManagedUsingKlaaryoModalFlagAtom(false)}
    >
      <ModalContent
        className="max-w-full w-full md:max-w-[640px] md:min-w-104"
        title={
          <div className="flex flex-col gap-3 justify-center items-center pb-6 px-3 lg:px-[6rem]">
            {jpData?.ats_applyurl ? (
              <div className="rounded-full bg-primary-50 p-2 flex items-center justify-center">
                <ArrowTopRightOnSquareIcon className="w-6 h-6 text-primary-700" />
              </div>
            ) : (
              <GreenCheckMarkWithWhiteBg
                {...{
                  height: 24,
                  width: 24,
                }}
              />
            )}
            <h4 className="text-center text-neutral-700 text-2xl font-bold">
              {jpData?.ats_applyurl
                ? t('ats_application_pending_completion_title')
                : t('your_application_has_been_successfully_sent')}
            </h4>
            {jpData?.ats_applyurl && (
              <p className="text-center text-neutral-500 text-sm font-normal">
                {t('ats_application_pending_completion_subtitle')}
              </p>
            )}
          </div>
        }
        footer={defaultFooter}
      >
        {jpData?.ats_applyurl ? (
          <AtsContinuationSteps />
        ) : (
          <NonAtsApplicationTimeline publishedFromAts={!!jpData?.published_from_ats} />
        )}
      </ModalContent>
    </Modal>
  );
};

// Native Restworld application path: the timeline shown after submission.
// Extracted into its own component so the `timelineSteps` useMemo only runs
// when this branch actually renders.
const NonAtsApplicationTimeline: React.FunctionComponent<{ publishedFromAts: boolean }> = ({ publishedFromAts }) => {
  const { t } = useTranslation('common');

  const router = useRouter();
  const { channel, status, jobPositionId } = useAtomValue(workerManagedInterviewAtom);
  const isWhatsapp = channel === 'whatsapp';
  // The way into the chat, only where there is one to finish: an in-app interview still open.
  const chatHref =
    channel === 'in_app' && status === 'pending' && jobPositionId ? preInterviewHref(jobPositionId) : null;

  const timelineSteps = React.useMemo(() => {
    type Step = {
      key?: number;
      title: string;
      checked: boolean;
      showDownIcon: boolean;
      verticalLineStyle: 'solid' | 'dashed';
      customContentElement?: React.ReactNode;
    };
    const steps: Step[] = [
      {
        key: 1,
        title: t('application_received'),
        checked: true,
        showDownIcon: false,
        verticalLineStyle: 'solid',
      },
      {
        key: 2,
        customContentElement: (
          <div className="px-3 py-2 rounded-lg border border-neutral-40 flex-col justify-center items-start gap-2.5 inline-flex">
            <h5 className="text-neutral-700 text-base font-medium">{t('interview_with_our_manager')}</h5>
            <div className="relative">
              <Image
                src={getAssetSrc('/utils-assets/virtual-manager-avatar.png')}
                width={56}
                height={56}
                alt="virtual-manager-avatar"
                loader={customImageLoader}
                className="rounded-full"
              />
              {isWhatsapp && (
                <Image
                  className="absolute top-[10px] left-[28px]"
                  src={getAssetSrc('/utils-assets/whatsapp-img.png')}
                  width={50}
                  height={50}
                  alt="whatsapp-icon"
                  loader={customImageLoader}
                />
              )}
            </div>
            <h5 className="text-gray-500 text-base font-medium">
              {isWhatsapp
                ? t('soon_to_be_contacted', {
                    virtual_manager: 'Serena',
                  })
                : t('interview_in_app_description')}
            </h5>
            {chatHref && (
              <Button size="sm" onClick={() => router.push(chatHref)}>
                {t('complete_the_interview')}
              </Button>
            )}
          </div>
        ),
        title: t('interview_with_our_manager'),
        checked: false,
        showDownIcon: true,
        verticalLineStyle: 'dashed',
      },
      {
        key: 3,
        title: t('send_application'),
        checked: false,
        showDownIcon: true,
        verticalLineStyle: 'dashed',
      },
      {
        key: 4,
        title: t('contact_with_the_company'),
        checked: false,
        showDownIcon: true,
        verticalLineStyle: 'dashed',
      },
    ];
    if (publishedFromAts) {
      // Remove step with key 2 and renumber the remaining ones
      return steps.filter((s) => s.key !== 2).map((s, idx) => ({ ...s, key: idx + 1 }));
    }
    return steps;
  }, [t, publishedFromAts, isWhatsapp, chatHref, router]);

  return (
    <div className="flex flex-col items-center relative px-3 lg:px-[6rem]">
      <TimelineV2 timeLineSteps={timelineSteps} />
    </div>
  );
};

// Two-step visual for ATS jobs: explains the worker has completed the profile
// on Restworld but still needs to finalize the application on the company's
// external portal. Designed to discourage a "I'm done" misread of the modal.
const AtsContinuationSteps: React.FunctionComponent = () => {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col gap-3 px-3 lg:px-[6rem]">
      <div className="flex items-start gap-3 p-4 rounded-lg border border-neutral-30 bg-neutral-10">
        <div className="shrink-0 rounded-full bg-green-50 p-2 flex items-center justify-center">
          <DocumentCheckIcon className="w-5 h-5 text-green-600" />
        </div>
        <div className="flex flex-col gap-1">
          <h5 className="text-neutral-700 text-base font-semibold">{t('ats_step_profile_saved_title')}</h5>
          <p className="text-neutral-500 text-sm">{t('ats_step_profile_saved_description')}</p>
        </div>
      </div>
      <div className="flex items-start gap-3 p-4 rounded-lg border border-primary-200 bg-primary-50">
        <div className="shrink-0 rounded-full bg-primary-100 p-2 flex items-center justify-center">
          <ArrowTopRightOnSquareIcon className="w-5 h-5 text-primary-700" />
        </div>
        <div className="flex flex-col gap-1">
          <h5 className="text-primary-800 text-base font-semibold">{t('ats_step_finalize_on_company_title')}</h5>
          <p className="text-primary-700 text-sm">{t('ats_step_finalize_on_company_description')}</p>
        </div>
      </div>
    </div>
  );
};

export default WorkerManagedUsingKlaaryo;
